import React, { useState, useEffect } from 'react';
import PageHeading from '../../components/PageHeading';
import {
  TeamOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import {
  Button,
  Input,
  message,
  Table,
  Modal,
  Form,
  Select,
  Switch,
  Popconfirm,
  Tag,
} from 'antd';
import axiosInstance from '../../utils/axios';

const AddEventStaff = () => {
  const [eventStaff, setEventStaff] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [modalVisible, setModalVisible] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    fetchEventStaff(pagination.current, search);
  }, [pagination.current, search]);

  const fetchEvents = async () => {
    try {
      const response = await axiosInstance.get('news_and_events/events/');  // List available events
      setEvents(response.data.results || response.data);
    } catch (error) {
      message.error('Failed to load events');
    }
  };

  const fetchEventStaff = async (page = 1, searchTerm = '') => {
    setTableLoading(true);
    try {
      let url = `news_and_events/staff/?page=${page}&page_size=${pagination.pageSize}`;
      if (searchTerm.trim()) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }

      const response = await axiosInstance.get(url);
      setEventStaff(response.data.results || []);
      setPagination((prev) => ({
        ...prev,
        current: page,
        total: response.data.count || 0,
      }));
    } catch (error) {
      message.error('Failed to fetch event staff members');
    } finally {
      setTableLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        email: values.email,
        name: values.name,
        mobile: values.mobile || null,
        event_id: values.event_id,
        is_active: values.is_active,
      };

      if (!editingStaff || values.password) {
        payload.password = values.password;
      }

      if (editingStaff) {
        await axiosInstance.patch(`news_and_events/staff/${editingStaff.id}/`, payload);
        message.success('Event staff updated successfully');
      } else {
        await axiosInstance.post('news_and_events/staff/', payload);
        message.success('Event staff added successfully');
      }

      form.resetFields();
      setModalVisible(false);
      setEditingStaff(null);
      fetchEventStaff(pagination.current, search);
    } catch (error) {
      const errMsg =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        JSON.stringify(error.response?.data) ||
        'Operation failed';
      message.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (record) => {
    if (!record.events || record.events.length === 0) {
      message.error('Cannot delete: no event assigned');
      return;
    }
    const eventId = record.events[0].id;

    try {
      await axiosInstance.delete(`news/staff/${eventId}/${record.id}/`);
      message.success('Event staff deleted successfully');
      fetchEventStaff(pagination.current, search);
    } catch (error) {
      message.error('Failed to delete event staff');
    }
  };

  const handleEdit = (record) => {
    setEditingStaff(record);
    const eventId = record.events[0]?.id;

    form.setFieldsValue({
      name: record.name,
      email: record.email,
      mobile: record.mobile || '',
      event_id: eventId,
      is_active: record.is_active,
    });
    setModalVisible(true);
  };

  const columns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    {
      title: 'Event',
      key: 'event',
      render: (_, record) =>
        record.events && record.events.length > 0 ? (
          <Tag color="purple">{record.events[0].heading}</Tag>
        ) : (
          '—'
        ),
    },
    { title: 'Mobile', dataIndex: 'mobile', key: 'mobile', render: (text) => text || '—' },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'status',
      render: (is_active) => (
        <span style={{ color: is_active ? '#52c41a' : '#f5222d' }}>
          {is_active ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      title: 'Action',
      key: 'actions',
      render: (_, record) => (
        <>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm
            title="Delete this event staff member?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <div className="dashboard">
      <PageHeading name="Event Staff Management" icon={<TeamOutlined />} />

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <Input
          placeholder="Search by name or email"
          prefix={<SearchOutlined />}
          style={{ width: '240px' }}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPagination((prev) => ({ ...prev, current: 1 }));
          }}
        />

        <Button
          type="primary"
          icon={<UserAddOutlined />}
          onClick={() => {
            setEditingStaff(null);
            form.resetFields();
            setModalVisible(true);
          }}
        >
          Add Event Staff
        </Button>
      </div>

      <Table
        dataSource={eventStaff}
        columns={columns}
        rowKey="id"
        loading={tableLoading}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          onChange: (page) => {
            setPagination((prev) => ({ ...prev, current: page }));
            fetchEventStaff(page, search);
          },
        }}
      />

      {/* Add / Edit Modal */}
      <Modal
        title={editingStaff ? 'Edit Event Staff Member' : 'Add New Event Staff Member'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingStaff(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          {/* Event Field - Disabled on Edit */}
          <Form.Item
            name="event_id"
            label="Assign Event"
            rules={[{ required: true, message: 'Please select one event' }]}
            normalize={(value) => (Array.isArray(value) ? value[0] : value)}
          >
            <Select
              placeholder="Select an event"
              disabled={!!editingStaff}
            >
              {events.map((ev) => (
                <Select.Option key={ev.id} value={ev.id}>
                  {ev.heading} ({ev.start_date} - {ev.end_date})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {/* Show current event name when editing */}
          {editingStaff && editingStaff.events && editingStaff.events.length > 0 && (
            <div style={{ marginBottom: 16, color: '#888', fontSize: 13 }}>
              Currently assigned to: <strong>{editingStaff.events[0].heading}</strong>
            </div>
          )}

          <Form.Item
            name="name"
            label="Full Name"
            rules={[{ required: true, message: 'Please enter full name' }]}
          >
            <Input placeholder="Enter full name" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Invalid email format' },
            ]}
          >
            <Input placeholder="Enter email address" />
          </Form.Item>

          <Form.Item
            name="mobile"
            label="Mobile Number"
            rules={[
              { required: true, message: 'Please enter mobile number' },
              { pattern: /^[0-9]{12}$/, message: 'Enter a valid 12-digit number' },
            ]}
          >
            <Input placeholder="e.g. 9876543210" maxLength={12} />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[
              {
                required: !editingStaff,
                message: 'Please enter password',
              },
            ]}
          >
            <Input.Password
              placeholder={
                editingStaff
                  ? 'Leave blank to keep current password'
                  : 'Set a strong password'
              }
            />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Status"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          <div style={{ textAlign: 'right', marginTop: 24 }}>
            <Button
              onClick={() => {
                setModalVisible(false);
                setEditingStaff(null);
                form.resetFields();
              }}
              style={{ marginRight: 8 }}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {editingStaff ? 'Update' : 'Add'} Event Staff
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default AddEventStaff;

